/**
 * Схема разбора упакованных UTM-меток, привязанная к рекламному кабинету (этап 3).
 *
 * Схема принадлежит кабинету: школа и источник наследуются от него, пользователь их не выбирает.
 * У кабинета РОВНО ОДНА схема — решение заказчика от 07.09.2026. Пользователь вставляет ссылку
 * объявления, система разбирает её и предлагает поля; глобальную библиотеку в интерфейсе
 * не показываем (на бэкенде она продолжает работать как запасной вариант разбора).
 * ТЗ бэка: docs/UTM_STAGE3_TZ.md, отличия — UTM_STAGE3_BACKEND_CHANGES.md
 */

/** Тип парсера схемы */
export const PARSER_TYPE = {
    positional: 1,
    keyValue: 2,
    alternating: 3,
    prefixed: 4,
} as const;

/** Подписи типов разбора для интерфейса */
export const PARSER_TYPE_LABELS: Record<number, string> = {
    [PARSER_TYPE.positional]: 'позиционный',
    [PARSER_TYPE.keyValue]: 'ключ:значение',
    [PARSER_TYPE.alternating]: 'чередование',
    [PARSER_TYPE.prefixed]: 'префиксный',
};

/** Статусы задачи переразбора (utm_parse_tasks.status) */
export const TASK_STATUS = {
    pending: 0,
    running: 1,
    done: 2,
    failed: 3,
} as const;

/** Итог работы демона по задаче */
export interface IUtmParseTaskResult {
    deleted?: number;
    scanned?: number;
    matched?: number;
    inserted?: number;
    cabinet?: number;
    duration_sec?: number;
    /** Текст ошибки при status = 3 */
    error?: string;
}

/** Задача переразбора */
export interface IUtmParseTask {
    id: number;
    task_type?: number;
    status: number;
    result?: IUtmParseTaskResult;
    created_at?: string;
    finished_at?: string;
}

/** Поле схемы: какой параметр достаём из этого места метки */
export interface IUtmSchemeField {
    field_key: string;
    /** Параметр из справочника utm_names (12..26); null — поле не используется */
    utm_name_id: number | null;
    is_ignored: boolean;
    sort_order: number;
}

/** Статистика схемы в разрезе текущей школы */
export interface IUtmSchemeStats {
    parsed_values: number;
    last_task?: IUtmParseTask | null;
}

/** Схема разбора */
export interface IUtmScheme {
    id: number;
    /** У глобальных схем кабинета нет */
    integration_id?: number | null;
    source_id: number;
    name: string;
    /** Метка, в которой лежит упакованное значение (utm_campaign и т.п.) */
    utm_name_id: number;
    signature: string;
    parser_type: number;
    separator: string | null;
    kv_separator: string | null;
    sample_url?: string | null;
    utm_source_value?: string | null;
    priority?: number;
    is_active: boolean;
    fields: IUtmSchemeField[];
    stats?: IUtmSchemeStats;
}

/**
 * Сводка по схеме кабинета в строке ответа POST /integration/items — поле `utm_scheme`.
 * Приходит в каждой строке при любых type/status_id, считается по всем кабинетным схемам
 * школы (активным и выключенным): `configured: true` сразу после создания,
 * `parsed_values: 0` до первого переразбора.
 */
export interface IUtmCabinetScheme {
    configured: boolean;
    scheme_id: number | null;
    name: string | null;
    is_active: boolean;
    parsed_values: number;
}

/**
 * Внутренний вид сводки, на котором построена ячейка таблицы. Схема у кабинета одна,
 * поэтому count интересует только как признак «настроена / не настроена», а names[0] —
 * её название. Прежний формат поля `utm_schemes` бэкенда имел ровно такой вид.
 */
export interface IUtmSchemesSummary {
    count: number;
    active: number;
    parsed_values: number;
    names: string[];
}

/** Откуда взялась догадка о параметре поля (analyze-url) */
export const FIELD_GUESS = {
    /** По имени подстановки {campaign_id} — уверенная */
    substitution: 'substitution',
    /** По виду значения реальной ссылки — стоит проверить */
    value: 'value',
    /** Константа формата (yd, fb) — поле не используется */
    constant: 'constant',
    /** Не распознано — поле не используется */
    unknown: 'unknown',
} as const;

/** Параметр из справочника utm_names (приходит в GET /utm-schemes/list) */
export interface IUtmNameOption {
    id: number;
    utm_name: string;
    label: string;
    kind?: number;
}

/** UTM-метка, в которой может лежать упакованное значение */
export interface IUtmLabelOption {
    id: number;
    utm_name: string;
}

/** Поле черновика: то же, что поле схемы, плюс значение из ссылки и происхождение догадки */
export interface IUtmDraftField extends IUtmSchemeField {
    /** Значение этого поля во вставленной ссылке */
    raw?: string;
    guess?: string;
    label?: string | null;
}

/** Схема, конфликтующая по сигнатуре (показываем справочно, сохранение не блокируется) */
export interface IUtmSchemeConflict {
    scheme_id: number;
    name: string;
    scope?: string;
}

/** Черновик схемы из разбора ссылки */
export interface IUtmSchemeDraft {
    name: string;
    utm_name_id: number | null;
    parser_type: number;
    separator: string | null;
    kv_separator: string | null;
    signature: string;
    fields: IUtmDraftField[];
    conflicts?: IUtmSchemeConflict[];
}

/** Метка из разобранной ссылки */
export interface IUtmAnalyzedLabel {
    utm_name: string;
    value: string;
    packed: boolean;
    draft?: IUtmSchemeDraft;
}

/** Одно значение словаря школы в предпросмотре */
export interface IUtmPreviewSample {
    /** id значения в словаре школы — уникальный ключ строки предпросмотра */
    utm_filter_id?: number;
    value: string;
    status: 'matched' | 'empty' | 'no_scheme';
    fields: Array<{ utm_name_id: number; label: string; value: string }>;
    broken: number;
    unknown_keys: string[];
}

/**
 * Ответ POST /utm-schemes/preview. `matched_total` считается до потолка в 100 000:
 * при `matched_total_capped` подходящих значений больше, чем показано в счётчике.
 * Максимальный `limit`, который примет бэкенд, — 50.
 */
export interface IUtmPreview {
    matched_total: number;
    matched_total_capped?: boolean;
    samples: IUtmPreviewSample[];
}

/** Сводка диагностики меток школы. Все числа, кроме `packed_values_total`, — за выбранный период */
export interface IUtmReportSummary {
    packed_values: number;
    /**
     * Упакованных значений за всю историю школы, независимо от периода.
     * Переразбор школы идёт по всему словарю, поэтому в подтверждении показываем это число.
     */
    packed_values_total?: number;
    parsed_values: number;
    no_scheme_values: number;
    broken_values: number;
}

/** Не сработавшая подстановка: в шаблоне ссылки забыли фигурные скобки */
export interface IUtmBrokenToken {
    token: string;
    utm_name_id: number | null;
    values: number;
}

/** Образец значения метки в диагностике */
export interface IUtmReportSample {
    utm_filter_id: number;
    utm_name_id: number;
    utm_name: string;
    value: string;
    /** Регистраций со значением за выбранный период */
    registrations: number;
    /** Дата последней регистрации со значением за всю историю; null — регистраций нет */
    last_registration_at?: string | null;
}

/** Формат упаковки, под который нет схемы (цифры заменены на N, кириллица на А) */
export interface IUtmNoSchemeShape {
    shape: string;
    values: number;
    sample: string;
}

/** Поле, в котором по всем кликам одно и то же значение */
export interface IUtmConstantField {
    scheme_id: number;
    scheme_name: string;
    utm_name_id: number;
    label: string;
    value: string;
    values: number;
    /** true — похоже на забытые фигурные скобки в шаблоне */
    suspicious: boolean;
}

/** Период отчёта диагностики: эхо запроса, обе границы null — показана вся история */
export interface IUtmDiagnosticsPeriod {
    from: string | null;
    to: string | null;
}

/** Ответ GET /utm-schemes/report */
export interface IUtmDiagnostics {
    period?: IUtmDiagnosticsPeriod;
    summary: IUtmReportSummary;
    broken: { tokens: IUtmBrokenToken[]; samples: IUtmReportSample[] };
    no_scheme: { shapes: IUtmNoSchemeShape[]; samples: IUtmReportSample[] };
    constant_fields: IUtmConstantField[];
}

/**
 * Ограничения бэкенда (UtmSchemesController). Держим их на фронте, чтобы пользователь
 * упирался в подсказку формы, а не в ошибку сохранения.
 */
export const LIMITS = {
    name: 200,
    signature: 500,
    separator: 4,
    sampleUrl: 4000,
    fieldKey: 60,
    url: 4000,
} as const;

/** Код ошибки создания второй схемы кабинета */
export const ERROR_SCHEME_EXISTS = 'scheme_exists';

/** Типы разбора, которым нужен разделитель имени и значения */
export const TYPES_WITH_KV_SEPARATOR: number[] = [ PARSER_TYPE.keyValue, PARSER_TYPE.prefixed ];

/**
 * Источники с упакованными метками, для которых показываем настройку схем.
 * ВРЕМЕННО только «Яндекс.Директ» (source_id = 2): у Facebook и myTarget схемы пока живут
 * только в глобальной библиотеке, кабинетные им не нужны. Признака «источник поддерживает
 * упаковку» бэкенд не отдаёт — решаем по source_id строки кабинета.
 * Тот же список ограничивает секции параметров в фильтрах (`VISIBLE_UTM_SOURCE_IDS`).
 */
export const UTM_SCHEMES_SOURCE_IDS: number[] = [ 2 ];

/** Поддерживает ли источник кабинета настройку схем разбора */
export const isUtmSchemesSource = (sourceId: unknown): boolean =>
    UTM_SCHEMES_SOURCE_IDS.includes(Number(sourceId));

/**
 * Приводит сводку из строки таблицы к безопасному виду: старый бэкенд поля не отдаёт,
 * а строки таблицы приходят и без него.
 */
export const normalizeSchemesSummary = (raw: unknown): IUtmSchemesSummary => {
    const summary = (raw ?? {}) as Partial<IUtmSchemesSummary> & Partial<IUtmCabinetScheme>;

    // Текущий формат бэкенда (поле utm_scheme): одна схема с признаком configured
    if (typeof summary.configured === 'boolean') {
        return {
            count: summary.configured ? 1 : 0,
            active: summary.configured && summary.is_active ? 1 : 0,
            parsed_values: Number(summary.parsed_values) || 0,
            names: summary.name ? [ summary.name ] : [],
        };
    }

    return {
        count: Number(summary.count) || 0,
        active: Number(summary.active) || 0,
        parsed_values: Number(summary.parsed_values) || 0,
        names: Array.isArray(summary.names) ? summary.names : [],
    };
};

/** Склонение слова «значение» по числу */
export const pluralizeValues = (count: number): string => {
    const lastTwo = count % 100;
    const last = count % 10;

    if (lastTwo >= 11 && lastTwo <= 14) return 'значений';
    if (last === 1) return 'значение';
    if (last >= 2 && last <= 4) return 'значения';
    return 'значений';
};
